/**
 * The report icon set.
 *
 * Inline SVG rather than an icon font or image assets, for three reasons the render
 * pipeline cares about: the PDF must embed nothing it fetches at print time (R4 — no
 * network at print), SSR must produce the whole document as markup, and a vector stays
 * crisp at 300dpi where a raster from the mockup would not.
 *
 * Names match what the templates already declare — `eye`, `cursor`, `audience`,
 * `retarget` have been in `display-template.ts` since M1; only the rendering was missing.
 *
 * Drawn to sit inside a circular badge, so each glyph is centred in a 24×24 viewBox with
 * consistent optical weight. `currentColor` throughout, so a theme change moves them
 * without touching this file.
 */

import * as React from 'react';
import { REPORT_ICON_IMAGES } from './report-icon-images.js';

/**
 * Which icon set the shared cards render: `legacy` (the inline-SVG glyphs the mockups were approved
 * with) or `v2` (the approved raster marks). Provided by `ReportDocument` from the template's
 * `theme.iconStyle`, so a template opts into the new marks per report; everything defaults to `legacy`
 * and is unchanged. Consumed by the cards that carry hard-coded marks (CampaignMetaCard, the device /
 * channel / browser cards) to pick the right icon names.
 */
export type IconStyle = 'legacy' | 'v2';
export const IconStyleContext = React.createContext<IconStyle>('legacy');

export type IconName =
  | 'eye' | 'cursor' | 'audience' | 'retarget' | 'play' | 'headphones'
  | 'person' | 'calendar' | 'hash' | 'monitor' | 'globe' | 'devices'
  | 'image' | 'link' | 'music' | 'gender' | 'age' | 'pin' | 'target'
  | 'display-ads' | 'retargeting' | 'email' | 'video' | 'dooh-screen'
  | 'person-solid' | 'calendar-solid' | 'hash-solid';

/**
 * A duotone accent stroke, for the parts the mockups draw in the accent colour rather than the icon
 * colour — the streaming sound waves, the retarget arrows. `currentColor` (the icon colour) carries
 * the rest, so a single glyph reads as two-tone without a second theme lookup.
 */
const ACCENT: React.CSSProperties = { stroke: 'var(--color-accent)' };

const P: Record<IconName, React.ReactNode> = {
  eye: <><path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z" /><circle cx="12" cy="12" r="3.4" /></>,
  cursor: <><path d="M7 3.8 17.2 9.6l-4.3 1.3-1.2 4.4L7 3.8Z" /><path d="M13.5 12.5 18 17" /><path d="M19.5 5.2 21 3.7M16.8 3.2l.4-2M21.8 8.4l2-.5" /></>,
  audience: <><circle cx="9" cy="8.5" r="3" /><path d="M3.2 19c0-3.2 2.6-5.2 5.8-5.2s5.8 2 5.8 5.2" /><circle cx="17" cy="9.5" r="2.3" /><path d="M15 14.2c2.9-.5 5.8 1 5.8 4.3" /></>,
  retarget: <><circle cx="12" cy="12" r="7.2" /><path d="M12 4.8V2M19.2 12H22" style={ACCENT} /><path d="M9.6 10.2 14.4 12l-2 .8-.8 2-2-4.6Z" /></>,
  play: <><circle cx="9" cy="12" r="5.5" /><path d="M7.6 9.6 11.4 12l-3.8 2.4V9.6Z" /><path d="M16.5 8.6a5 5 0 0 1 0 6.8M19 6.4a8.4 8.4 0 0 1 0 11.2" style={ACCENT} /></>,
  headphones: <><path d="M4 15v-3a8 8 0 0 1 16 0v3" /><rect x="2.5" y="14" width="4" height="6" rx="1.6" /><rect x="17.5" y="14" width="4" height="6" rx="1.6" /></>,
  person: <><circle cx="12" cy="8.6" r="3.4" /><path d="M5.5 19.5c0-3.6 2.9-5.9 6.5-5.9s6.5 2.3 6.5 5.9" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 9.6h17M8.5 3v4M15.5 3v4" /></>,
  hash: <><path d="M9.2 3.5 7 20.5M17 3.5l-2.2 17M4 8.8h16.5M3.2 15.2h16.5" /></>,
  monitor: <><rect x="2.5" y="4.5" width="19" height="12.5" rx="1.8" /><path d="M9 20.5h6M12 17v3.5" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 3.8 5.8 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.8-3.8-9S9.5 5.6 12 3Z" /></>,
  devices: <><rect x="2" y="5" width="14" height="10" rx="1.6" /><path d="M6 18.5h6" /><rect x="16.5" y="9" width="5.5" height="10.5" rx="1.4" /></>,
  image: <><rect x="3" y="4.5" width="18" height="15" rx="2" /><circle cx="8.4" cy="9.6" r="1.8" /><path d="m3.6 17 5.2-5 4 3.6 3.4-2.8 4.4 4.2" /></>,
  link: <><path d="M10.2 13.8a4 4 0 0 0 5.7 0l2.9-2.9a4 4 0 1 0-5.7-5.7l-1.3 1.3" /><path d="M13.8 10.2a4 4 0 0 0-5.7 0l-2.9 2.9a4 4 0 1 0 5.7 5.7l1.3-1.3" /></>,
  music: <><path d="M9 18V6.2l10-2v11.4" /><circle cx="6.6" cy="18" r="2.6" /><circle cx="16.6" cy="15.6" r="2.6" /></>,
  gender: <><circle cx="10.4" cy="13.6" r="5.2" /><path d="M14.4 9.6 20.5 3.5M15.5 3.5h5v5" /></>,
  age: <><circle cx="9" cy="8.5" r="3" /><path d="M3.2 19c0-3.2 2.6-5.2 5.8-5.2s5.8 2 5.8 5.2" /><path d="M17.5 12.5h4M19.5 10.5v4" /></>,
  pin: <><path d="M12 21.5s7-6.2 7-11.2a7 7 0 1 0-14 0c0 5 7 11.2 7 11.2Z" /><circle cx="12" cy="10" r="2.6" /></>,
  target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.6" /><circle cx="12" cy="12" r="1" /></>,

  /*
   * Channel marks.
   *
   * Drawn heavier than the row icons because the mockup sets these large in the
   * Campaign Channels card, where they are the primary content rather than a bullet.
   */
  'display-ads': <><rect x="2" y="4.6" width="20" height="13.4" rx="1.8" /><path d="M5.4 8.6h6M5.4 11.6h6M5.4 14.4h4" /><rect x="13.6" y="8.2" width="5.4" height="4.4" rx="0.8" /><path d="M8.5 21h7" /></>,
  retargeting: <><circle cx="12" cy="12" r="6.4" /><path d="M12 3.2V1M12 23v-2.2M3.2 12H1M23 12h-2.2" /><path d="M9.9 10.4 14.6 12l-2 .9-.9 2-1.8-4.5Z" /><path d="M17.6 6.4a8 8 0 0 1 2 4.2M6.4 17.6a8 8 0 0 1-2-4.2" style={ACCENT} /></>,
  email: <><rect x="2.4" y="5" width="19.2" height="14" rx="2" /><path d="m2.9 6.6 9.1 6.6 9.1-6.6" /></>,
  video: <><rect x="2.4" y="5.4" width="13.6" height="13.2" rx="2" /><path d="m16.4 10.4 5.2-3v9.2l-5.2-3Z" /></>,
  'dooh-screen': <><rect x="2.6" y="3.6" width="18.8" height="12.4" rx="1.6" /><path d="M12 16v5M8 21h8" /><path d="M6.6 7.4h6M6.6 10.4h8" /></>,

  /*
   * Solid variants, for the campaign-details rows.
   *
   * The mockup sets those inside an outlined ring, and a thin stroked glyph disappears
   * against the ring at that size — the two weights compete and the badge reads as a
   * smudge. Filling the glyph gives the ring something to contain.
   */
  'person-solid': <><circle cx="12" cy="8.2" r="3.6" fill="currentColor" stroke="none" /><path d="M5.4 19.4c0-3.7 3-6 6.6-6s6.6 2.3 6.6 6Z" fill="currentColor" stroke="none" /></>,
  'calendar-solid': <><rect x="3.6" y="5.2" width="16.8" height="15" rx="2.2" /><path d="M3.6 9.8h16.8M8.4 3.2v4M15.6 3.2v4" /><g fill="currentColor" stroke="none"><rect x="6.6" y="12" width="2.2" height="2.2" rx="0.4" /><rect x="10.9" y="12" width="2.2" height="2.2" rx="0.4" /><rect x="15.2" y="12" width="2.2" height="2.2" rx="0.4" /><rect x="6.6" y="15.8" width="2.2" height="2.2" rx="0.4" /><rect x="10.9" y="15.8" width="2.2" height="2.2" rx="0.4" /></g></>,
  'hash-solid': <><path d="M9.4 3.6 7.2 20.4M17 3.6l-2.2 16.8M4 9h16.4M3.2 15h16.4" strokeWidth={2.4} /></>,
};

/**
 * The device illustration from the mockup: a monitor with a phone in front of it.
 *
 * Its own component rather than an `Icon`, because it is set much larger and is drawn to
 * a wider aspect — squeezing it into the 24×24 grid the row icons share would either
 * crop it or make it a thumbnail of itself.
 */
export function DeviceIllustration({ height = 46 }: { height?: number }) {
  return (
    <svg width={height * 1.35} height={height} viewBox="0 0 68 50" fill="none"
         stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      <rect x="2" y="3" width="46" height="30" rx="2.4" />
      <path d="M18 40h14M25 33v7" />
      <path d="M6.5 8h30M6.5 12.5h22" opacity="0.45" />
      <rect x="45" y="18" width="21" height="30" rx="3" fill="#fff" />
      <path d="M52 22.5h7" />
      <circle cx="55.5" cy="43.5" r="1.6" />
    </svg>
  );
}

/**
 * The legacy inline-SVG glyph a V2 slot key falls back to on a NON-V2 report.
 *
 * Templates and cards name icons by their V2 slot (`kpi-targeted-impressions`, `banner-geo-targeting`,
 * …). On a report whose `theme.iconStyle` is not `v2`, those names resolve here to the glyph the
 * mockups were approved with, so a template can carry V2 slot keys and still render its legacy look on
 * a legacy report — the plain display template stays byte-identical while V2 — Agency Display (which
 * clones it and sets `iconStyle: 'v2'`) shows the raster marks. A key with no entry renders nothing on
 * a legacy report, which is only reachable if a new slot is added without a fallback.
 */
const LEGACY_ICON_FOR: Record<string, IconName> = {
  'kpi-targeted-impressions': 'eye', 'kpi-total-clicks': 'cursor',
  'kpi-retargeted-impressions': 'audience', 'kpi-retargeted-clicks': 'retarget',
  'kpi-total-streams': 'play', 'kpi-video-impressions': 'video',
  'banner-geo-targeting': 'globe', 'banner-audience-targeting': 'audience', 'banner-top-sites': 'globe',
  'banner-campaign-details': 'image', 'banner-audio-summary': 'headphones', 'banner-music-genres': 'music',
  'banner-language': 'globe', 'banner-age-group': 'age', 'banner-gender': 'gender',
  'banner-device-category': 'devices', 'banner-campaign-channels': 'monitor', 'banner-browser-type': 'globe',
  'audio-ads-played': 'play', 'audio-reach': 'target', 'audio-ltr': 'headphones',
  'audio-image-dimensions': 'image', 'audio-destination-url': 'globe', 'audio-clicks': 'cursor',
  'dooh-geo-targeting': 'pin', 'dooh-contracted-impressions': 'eye', 'dooh-actual-impressions': 'eye',
  'meta-client': 'person-solid', 'meta-start': 'calendar-solid', 'meta-end': 'calendar-solid',
  'meta-campaign-id': 'hash-solid',
};

/** Resolve a name to the glyph to draw, given the icon style: a V2 slot key falls back to its glyph. */
function svgNameFor(name: string | undefined, v2: boolean): IconName | undefined {
  if (!name) return undefined;
  if (P[name as IconName]) return name as IconName;      // already a glyph name
  if (!v2 && REPORT_ICON_IMAGES[name]) return LEGACY_ICON_FOR[name]; // a V2 slot key on a legacy report
  return undefined;
}

/**
 * Silently omitted when unknown, so an unrecognised name never breaks a layout.
 *
 * On a V2 report (`theme.iconStyle: 'v2'`, provided via `IconStyleContext`), a name that resolves in
 * `REPORT_ICON_IMAGES` renders the approved PNG mark (inlined data URI). Otherwise it renders the inline
 * SVG glyph — mapping a V2 slot key back to its approved-mockup glyph so a legacy report is unchanged.
 */
export function Icon({ name, size = 18 }: { name?: string; size?: number }) {
  const v2 = React.useContext(IconStyleContext) === 'v2';
  const img = name && v2 ? REPORT_ICON_IMAGES[name] : undefined;
  if (img) {
    return (
      <img className="report-icon-img" src={img} width={size} height={size}
           style={{ objectFit: 'contain', display: 'inline-block', verticalAlign: 'middle' }}
           alt="" aria-hidden="true" />
    );
  }
  const path = P[svgNameFor(name, v2) as IconName];
  if (!path) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      {path}
    </svg>
  );
}

/**
 * The circular badge the mockups put behind a KPI icon. A V2 image mark carries its own art, so it is
 * rendered larger and WITHOUT the badge disc; the legacy glyph keeps the disc. The multiplier matches the
 * mockups' prominent KPI marks — the raster art is 128px source, so it stays crisp at this size.
 */
export function IconBadge({ name, size = 20 }: { name?: string; size?: number }) {
  const v2 = React.useContext(IconStyleContext) === 'v2';
  if (name && v2 && REPORT_ICON_IMAGES[name]) return <Icon name={name} size={Math.round(size * 2.3)} />;
  if (!svgNameFor(name, v2)) return null;
  return <span className="icon-badge"><Icon name={name} size={size} /></span>;
}
